import React, { useState, useEffect, useRef, useMemo } from 'react';
import { formatBrandMoney, getBrandDatePickerLocale } from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableHeadClass,
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import {
  buildHrColumnVisibilityState,
  buildHrQawaemColumnDefs,
  mapHrStatCards,
  buildHrImportExportProps,
  buildHrControlsStrip,
  isHrColumnVisible,
  createHrHandleToggleColumn,
  hrTableColSpan,
  useHrModalBodyLock,
  HrFormModalTabBar,
  useHrTableRowSelection,
  HrBulkDeleteFilterAction,
  sliceHrListItems,
  resolveHrDepartmentLabel,
  previewHrEmployeeMatricule,
  useHrDepartments,
  useHrEmployees,
} from './hrListPageUtils';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { useHrConfig } from './useHrConfig';
import { dispatchHrEmployeesRefresh } from './hrRefreshEvents';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import {
  SAUDI_CITIES,
  NITAQAT_CATEGORIES,
  SAUDI_CONTRACT_TYPES,
  SAUDI_PROBATION_DAYS_DEFAULT,
  formatHrDate,
  getHrDateLocale,
  getHrPhonePlaceholder,
  getDefaultSaudiEmployeeFields,
  getSaudiContractOptions,
  getSaudiContractLabel,
  normalizeEmployeeForSaudi,
  normalizeLegacyContractType,
  validateHrPhone,
  normalizeHrPhone,
} from './utils/hrSaudiFormat';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Search, Plus, Edit, Trash2, RefreshCcw, Briefcase, AlertTriangle, AlertCircle, CheckCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Shield, Layers, Gauge, FileText, Clock, List, LayoutList, Check, UserCheck, Building, UserX, TrendingUp, Camera, User } from 'lucide-react';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { buildExportFilename } from '../../shared/utils/exportFilename';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ar from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { mergeStickySelectClass, QawaemTableStickyActionsTd } from '../../products/saudi/components/QawaemTableStickyActions';

registerLocale('ar', ar);

const HR_TABLE_COLUMNS = [
  { key: 'mat', labelKey: 'col_matricule', required: true },
  { key: 'nom', labelKey: 'col_nom', required: true },
  { key: 'prenom', labelKey: 'col_prenom' },
  { key: 'fonction', labelKey: 'col_fonction' },
  { key: 'affectation', labelKey: 'col_department' },
  { key: 'dateRecrutement', labelKey: 'col_recruitment_date' },
  { key: 'typeContrat', labelKey: 'col_contract' },
  { key: 'status', labelKey: 'col_status' },
  { key: 'actions', labelKey: 'col_actions', required: true },
];

const EmployeesPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.ressources_humaines.pages.employeesPage.${key}`, opts);
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const dateLocale = getHrDateLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const phonePlaceholder = getHrPhonePlaceholder();
  const saudiFieldDefaults = getDefaultSaudiEmployeeFields();
  const hrConfig = useHrConfig();
  const { departments } = useHrDepartments();
  const { employees: hrEmployees, refresh: refreshEmployees } = useHrEmployees();
  const employees = useMemo(
    () => sortRecordsNewestFirst(hrEmployees),
    [hrEmployees],
  );

  const formatSalaryDisplay = (val) => {
    if (val == null || val === '') return null;
    const n = parseFloat(val);
    if (Number.isNaN(n)) return p('na');
    return formatBrandMoney(n);
  };

  const getGenderLabel = (sexe) => {
    if (sexe === 'M' || sexe === 'Homme') return p('gender_male');
    if (sexe === 'F' || sexe === 'Femme') return p('gender_female');
    return sexe || p('na');
  };

  const getMaritalLabel = (status) => {
    const map = {
      'single': p('marital_single'),
      'married': p('marital_married'),
      'divorced': p('marital_divorced'),
      'widowed': p('marital_widowed'),
    };
    return map[status] || status || p('na');
  };

  const getStatusLabel = (status) => {
    const map = { active: p('status_active'), leave: p('status_leave'), inactive: p('status_inactive') };
    return map[status] || p('na');
  };

  const getContractLabel = (type) => getSaudiContractLabel(type, t);

  const contractOptions = useMemo(() => getSaudiContractOptions(t), [t]);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOnStatusButton = event.target.closest('[data-employee-status-dropdown-button]');
      const isClickInStatusDropdown = event.target.closest('[data-employee-status-dropdown]');
      if (!isClickOnStatusButton && !isClickInStatusDropdown) {
        setShowStatusDropdown(false);
      }
      
      const isClickOnDepartmentButton = event.target.closest('[data-employee-department-dropdown-button]');
      const isClickInDepartmentDropdown = event.target.closest('[data-employee-department-dropdown]');
      if (!isClickOnDepartmentButton && !isClickInDepartmentDropdown) {
        setShowDepartmentDropdown(false);
      }
      
      const isClickOnContractButton = event.target.closest('[data-employee-contract-dropdown-button]');
      const isClickInContractDropdown = event.target.closest('[data-employee-contract-dropdown]');
      if (!isClickOnContractButton && !isClickInContractDropdown) {
        setShowContractDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useState('table');
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  useHrModalBodyLock(showModal, showViewModal);
  
  const [formTab, setFormTab] = useState('personalInfo');
  
  const [formData, setFormData] = useState({ 
    mat: '',
    nom: '',
    prenom: '',
    dateNaissance: '',
    lieuNaissance: '',
    sexe: '',
    situationFamiliale: '',
    enfants: '',
    adresse: '',
    ville: '',
    codePostal: '',
    telephone: '',
    email: '',
    ...saudiFieldDefaults,
    fonction: '',
    posteOccupe: '',
    categorieProfessionnelle: '',
    affectation: '',
    emplacement: '',
    dateRecrutement: '',
    dateSortie: '',
    typeContrat: SAUDI_CONTRACT_TYPES.UNLIMITED,
    periodeEssai: String(SAUDI_PROBATION_DAYS_DEFAULT),
    finEssai: '',
    salaireBase: '',
    salaireNetMensuel: '',
    indemniteExperience: '',
    indemniteTransport: '',
    indemnitePanier: '',
    primeResponsabilite: '',
    primeRendementIndividuel: '',
    primeRendementCollectif: '',
    status: 'active',
    canApprove: false, // Can approve documents (for "Bon pour accord")
    profilePicture: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showDepartmentDropdown, setShowDepartmentDropdown] = useState(false);
  const [showContractDropdown, setShowContractDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_employees');
  const [quickFilter, setQuickFilter] = useState('all');
  
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const departmentDropdownRef = useRef(null);
  const contractDropdownRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      
      if (!event.target.closest('.relative')) {      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper to get department name from ID
  const getDepartmentName = (deptId) => {
    if (!deptId) return p('na');
    return resolveHrDepartmentLabel(departments, deptId) || p('na');
  };

  const stats = useMemo(() => ({
    total: employees.length,
    active: employees.filter(e => e.status === 'active').length,
    onLeave: employees.filter(e => e.status === 'leave').length,
    newHires: employees.filter(e => {
      if (!e.dateRecrutement) return false;
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      return new Date(e.dateRecrutement) > threeMonthsAgo;
    }).length,
    departments: [...new Set(employees.map(e => e.affectation).filter(Boolean))].length,
  }), [employees]);

  const mainTabs = useMemo(() => [
    { id: 'all_employees', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_departement', name: p('tab_by_department'), icon: Building },
    { id: 'par_anciennete', name: p('tab_by_seniority'), icon: TrendingUp },
  ], [t]);

  const quickFilterButtons = useMemo(() => [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'active', name: p('filter_active'), icon: UserCheck },
    { id: 'newHires', name: p('filter_new_hires'), icon: CheckCircle },
    { id: 'onLeave', name: p('filter_on_leave'), icon: AlertCircle },
  ], [t]);

  const employeesDistribution = useMemo(() => ({
    all_employees: {
      title: p('overview_title'),
      data: [
        { filterKey: 'active', name: p('dist_active'), count: stats.active, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'leave', name: p('dist_on_leave'), count: stats.onLeave, color: 'bg-gradient-to-r from-amber-500 to-amber-600' },
        { filterKey: 'new_hires_3m', name: p('dist_new_hires_3m'), count: stats.newHires, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
      ],
    },
    par_departement: {
      title: p('dist_by_department_title'),
      data: (() => {
        const colors = [
          'bg-gradient-to-r from-blue-500 to-blue-600',
          'bg-gradient-to-r from-purple-500 to-purple-600',
          'bg-gradient-to-r from-green-500 to-green-600',
          'bg-gradient-to-r from-cyan-500 to-cyan-600',
          'bg-gradient-to-r from-indigo-500 to-indigo-600',
          'bg-gradient-to-r from-pink-500 to-pink-600',
          'bg-gradient-to-r from-teal-500 to-teal-600',
        ];
        return departments.map((dept, idx) => ({
          filterKey: dept.id,
          name: dept.name,
          count: employees.filter(e => e.affectation === dept.id).length,
          color: colors[idx % colors.length],
        }));
      })(),
    },
    par_anciennete: {
      title: p('dist_by_seniority_title'),
      data: [
        { filterKey: 'less_1_year', name: p('dist_less_1_year'), count: employees.filter(e => {
          if (!e.dateRecrutement) return false;
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          return new Date(e.dateRecrutement) > oneYearAgo;
        }).length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: '1_5_years', name: p('dist_1_5_years'), count: employees.filter(e => {
          if (!e.dateRecrutement) return false;
          const oneYearAgo = new Date();
          const fiveYearsAgo = new Date();
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
          const date = new Date(e.dateRecrutement);
          return date <= oneYearAgo && date > fiveYearsAgo;
        }).length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'more_5_years', name: p('dist_more_5_years'), count: employees.filter(e => {
          if (!e.dateRecrutement) return false;
          const fiveYearsAgo = new Date();
          fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
          return new Date(e.dateRecrutement) <= fiveYearsAgo;
        }).length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
      ],
    },
  }), [employees, departments, stats, t]);

  const distributionInfo = employeesDistribution[mainViewMode] || employeesDistribution.all_employees;

  const baseFilteredEmployees = employees.filter(e => {
    // Search filter
    if (searchTerm && !(
      e.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.mat?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.fonction?.toLowerCase().includes(searchTerm.toLowerCase())
    )) return false;
    
    // Quick filter (only for Vue Générale)
    if (mainViewMode === 'all_employees') {
      if (quickFilter === 'active' && e.status !== 'active') return false;
      if (quickFilter === 'onLeave' && e.status !== 'leave') return false;
      if (quickFilter === 'newHires') {
        if (!e.dateRecrutement) return false;
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        if (new Date(e.dateRecrutement) <= threeMonthsAgo) return false;
      }
    }
    
    // Distribution card filter (clickable stats)
    if (activeDistributionFilter) {
      switch(mainViewMode) {
        case 'all_employees':
          if (activeDistributionFilter === 'active' && e.status !== 'active') return false;
          if (activeDistributionFilter === 'leave' && e.status !== 'leave') return false;
          if (activeDistributionFilter === 'new_hires_3m') {
            if (!e.dateRecrutement) return false;
            const threeMonthsAgo = new Date();
            threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
            if (new Date(e.dateRecrutement) <= threeMonthsAgo) return false;
          }
          break;
        case 'par_departement':
          if (activeDistributionFilter && e.affectation !== activeDistributionFilter) return false;
          break;
        case 'par_anciennete':
          if (!e.dateRecrutement) return false;
          const date = new Date(e.dateRecrutement);
          const oneYearAgo = new Date();
          const fiveYearsAgo = new Date();
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
          if (activeDistributionFilter === 'less_1_year' && date <= oneYearAgo) return false;
          if (activeDistributionFilter === '1_5_years' && (date > oneYearAgo || date <= fiveYearsAgo)) return false;
          if (activeDistributionFilter === 'more_5_years' && date > fiveYearsAgo) return false;
          break;
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(() => [
    textColumnRule('nom', (r) => r.nom || ''),
    textColumnRule('prenom', (r) => r.prenom || ''),
    textColumnRule('fonction', (r) => r.fonction || ''),
    textColumnRule('dateRecrutement', (r) => (r.dateRecrutement ? formatHrDate(r.dateRecrutement, i18n.language) : '')),
    exactColumnRule('status', (r) => r.status || ''),
  ], [i18n.language]);

  const filteredEmployees = useMemo(
    () => sliceHrListItems(
      applyColumnFilters(baseFilteredEmployees, colFilters, columnFilterRules),
      hrConfig.itemsPerPage,
    ),
    [baseFilteredEmployees, colFilters, columnFilterRules, hrConfig.itemsPerPage],
  );

  const { allRowsSelected, someRowsSelected, handleSelectAll, toggleRecord: handleSelectRecord } =
    useHrTableRowSelection(filteredEmployees, selectedRecords, setSelectedRecords);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nom?.trim() || !formData.prenom?.trim()) {
      alert(p('save_error', { message: p('required_name', { defaultValue: 'الاسم واللقب مطلوبان' }) }));
      return;
    }
    if (formData.telephone && !validateHrPhone(formData.telephone)) {
      alert(p('invalid_phone'));
      return;
    }
    try {
      const submitData = {
        ...formData,
        telephone: normalizeHrPhone(formData.telephone),
        typeContrat: normalizeLegacyContractType(formData.typeContrat),
        enfants: parseInt(formData.enfants, 10) || 0,
        salaireBase: parseFloat(formData.salaireBase) || 0,
        salaireNetMensuel: parseFloat(formData.salaireNetMensuel) || 0,
        indemniteExperience: parseFloat(formData.indemniteExperience) || 0,
        indemniteTransport: parseFloat(formData.indemniteTransport) || 0,
        indemnitePanier: parseFloat(formData.indemnitePanier) || 0,
        primeResponsabilite: parseFloat(formData.primeResponsabilite) || 0,
        primeRendementIndividuel: parseFloat(formData.primeRendementIndividuel) || 0,
        primeRendementCollectif: parseFloat(formData.primeRendementCollectif) || 0,
      };

      if (editingRecord) {
        await localApiAdapter.put(`/hr/employees/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'الموظف' }));
      } else {
        await localApiAdapter.post('/hr/employees', submitData);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'الموظف' }));
      }
      resetForm();
      refreshEmployees();
      dispatchHrEmployeesRefresh();
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('save_error', { message: error.message || p('unknown_error') }));
    }
  };

  const handleEdit = (record) => {
    const normalized = normalizeEmployeeForSaudi(record);
    setEditingRecord(record);
    setFormData({
      mat: normalized.mat || '',
      nom: normalized.nom || '',
      prenom: normalized.prenom || '',
      dateNaissance: normalized.dateNaissance || '',
      lieuNaissance: normalized.lieuNaissance || '',
      sexe: normalized.sexe || '',
      situationFamiliale: normalized.situationFamiliale || '',
      enfants: normalized.enfants || '',
      adresse: normalized.adresse || '',
      ville: normalized.ville || normalized.city || '',
      codePostal: normalized.codePostal || '',
      telephone: normalized.telephone || '',
      email: normalized.email || '',
      nationalityType: normalized.nationalityType,
      nationalId: normalized.nationalId || '',
      iqamaNumber: normalized.iqamaNumber || '',
      iqamaExpiry: normalized.iqamaExpiry || '',
      passportNumber: normalized.passportNumber || '',
      passportExpiry: normalized.passportExpiry || '',
      sponsorship: normalized.sponsorship || '',
      workPermitNumber: normalized.workPermitNumber || '',
      workPermitExpiry: normalized.workPermitExpiry || '',
      exitReentryVisa: normalized.exitReentryVisa || '',
      exitReentryExpiry: normalized.exitReentryExpiry || '',
      gosiNumber: normalized.gosiNumber || '',
      nitaqatCategory: normalized.nitaqatCategory || '',
      iban: normalized.iban || '',
      region: normalized.region || '',
      fonction: normalized.fonction || '',
      posteOccupe: normalized.posteOccupe || '',
      categorieProfessionnelle: normalized.categorieProfessionnelle || '',
      affectation: normalized.affectation || '',
      dateRecrutement: normalized.dateRecrutement || '',
      dateSortie: normalized.dateSortie || '',
      typeContrat: normalized.typeContrat || SAUDI_CONTRACT_TYPES.UNLIMITED,
      periodeEssai: normalized.periodeEssai || String(SAUDI_PROBATION_DAYS_DEFAULT),
      finEssai: normalized.finEssai || '',
      salaireBase: normalized.salaireBase || '',
      salaireNetMensuel: normalized.salaireNetMensuel || '',
      indemniteExperience: normalized.indemniteExperience || '',
      indemniteTransport: normalized.indemniteTransport || '',
      indemnitePanier: normalized.indemnitePanier || '',
      primeResponsabilite: normalized.primeResponsabilite || '',
      primeRendementIndividuel: normalized.primeRendementIndividuel || '',
      primeRendementCollectif: normalized.primeRendementCollectif || '',
      status: normalized.status || 'active',
      canApprove: normalized.canApprove || false,
      profilePicture: normalized.profilePicture || '',
    });
    setFormTab('personalInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/hr/employees/${id}`);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الموظف' }));
        refreshEmployees();
        dispatchHrEmployeesRefresh();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('delete_error', { defaultValue: 'تعذّر حذف الموظف' }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/hr/employees/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'موظف' }));
        setSelectedRecords([]);
        refreshEmployees();
        dispatchHrEmployeesRefresh();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('bulk_delete_error', { defaultValue: 'تعذّر حذف الموظفين المحددين' }));
      }
    }
  };

  const buildEmptyEmployeeForm = (matValue = '') => ({
    mat: matValue,
    nom: '',
    prenom: '',
    dateNaissance: '',
    lieuNaissance: '',
    sexe: '',
    situationFamiliale: '',
    enfants: '',
    adresse: '',
    ville: '',
    codePostal: '',
    telephone: '',
    email: '',
    ...getDefaultSaudiEmployeeFields(),
    fonction: '',
    posteOccupe: '',
    categorieProfessionnelle: '',
    affectation: '',
    emplacement: '',
    dateRecrutement: '',
    dateSortie: '',
    typeContrat: SAUDI_CONTRACT_TYPES.UNLIMITED,
    periodeEssai: String(SAUDI_PROBATION_DAYS_DEFAULT),
    finEssai: '',
    salaireBase: '',
    salaireNetMensuel: '',
    indemniteExperience: '',
    indemniteTransport: '',
    indemnitePanier: '',
    primeResponsabilite: '',
    primeRendementIndividuel: '',
    primeRendementCollectif: '',
    status: 'active',
    canApprove: false,
    profilePicture: '',
  });

  const resetForm = () => {
    setFormData(buildEmptyEmployeeForm());
    setEditingRecord(null);
    setFormTab('personalInfo');
    setShowModal(false);
  };

  const employeeExportHeaders = () => ([
    { key: 'mat', label: p('col_matricule') },
    { key: 'nom', label: p('col_nom') },
    { key: 'prenom', label: p('col_prenom') },
    { key: 'fonction', label: p('col_fonction') },
    { key: 'affectation', label: p('col_department') },
    { key: 'dateRecrutement', label: p('col_recruitment_date') },
    { key: 'status', label: p('col_status') },
  ]);

  // Export/Import handlers
  const handleExportCSV = () => {
    const headers = employeeExportHeaders();
    const dataToExport = filteredEmployees;
    const fileName = buildExportFilename('common.export_files.filename_prefix.employees', { ext: 'csv' });
    try {
      exportToCSV(dataToExport, headers, fileName);
    } catch (error) {
      console.error('Export error:', error);
      alert(p('export_error'));
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const filteredData = filteredEmployees;
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('col_matricule')}</th>
                <th>${p('col_nom')}</th>
                <th>${p('col_prenom')}</th>
                <th>${p('col_fonction')}</th>
                <th>${p('col_department')}</th>
                <th>${p('col_status')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.mat || p('na')}</td>
                  <td>${item.nom || ''}</td>
                  <td>${item.prenom || ''}</td>
                  <td>${item.fonction || ''}</td>
                  <td>${item.affectation || ''}</td>
                  <td>${getStatusLabel(item.status)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      document.body.appendChild(tempDiv);
      await generateReportPDF(p('report_pdf_title'), {});
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert(p('pdf_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    const headers = employeeExportHeaders();
    const fileName = buildExportFilename('common.export_files.filename_prefix.employees', { ext: 'xlsx' });
    try {
      setIsGeneratingExcel(true);
      await exportToExcel(filteredEmployees, headers, fileName, p('export_sheet_name'));
    } catch (error) {
      console.error('Excel generation error:', error);
      alert(p('excel_error'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleImport = () => {
    const headers = [
      { key: 'mat', label: p('col_matricule') },
      { key: 'nom', label: p('col_nom') },
      { key: 'prenom', label: p('col_prenom') },
      { key: 'fonction', label: p('col_fonction') },
      { key: 'affectation', label: p('col_department') },
    ];
    
    const schema = {
      requiredFields: ['nom', 'prenom'],
      validators: {
        status: (value) => !value || ['active', 'leave', 'inactive'].includes(value)
      }
    };
    
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/hr/employees', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          await refreshEmployees();
          dispatchHrEmployeesRefresh();
          const message = p('import_success', { total: result.totalCount, imported: successCount }) +
            (result.invalidCount > 0 ? '\n' + p('import_errors_line', { count: result.invalidCount, details: result.errors.slice(0, 5).join('\n') }) : '');
          alert(message);
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('import_error'));
      }
    }, '.csv,.xlsx,.xls');
  };

  const statusOptions = useMemo(() => [
    { value: 'active', label: p('status_active'), icon: UserCheck, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'leave', label: p('status_leave'), icon: AlertCircle, style: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    { value: 'inactive', label: p('status_inactive'), icon: UserX, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ], [t]);

  const kpiStats = useMemo(() => ([
    { key: 'total', label: p('stat_total'), value: stats.total, icon: Users, variant: 'copper' },
    { key: 'active', label: p('stat_active'), value: stats.active, icon: UserCheck, variant: 'forest' },
    { key: 'newHires', label: p('stat_new_hires'), value: stats.newHires, icon: CheckCircle, variant: 'copper' },
    { key: 'onLeave', label: p('stat_on_leave'), value: stats.onLeave, icon: AlertCircle, variant: 'forest' },
  ]), [stats, p]);

  const listStats = useMemo(() => mapHrStatCards(kpiStats), [kpiStats]);

  const employeeStatusFilterOptions = useMemo(
    () => statusOptions.map((s) => ({ value: s.value, label: s.label })),
    [statusOptions],
  );

  const [columnVisibility, setColumnVisibility] = useState(() =>
    buildHrColumnVisibilityState(HR_TABLE_COLUMNS),
  );
  const qawaemColumns = useMemo(
    () => buildHrQawaemColumnDefs(HR_TABLE_COLUMNS, p, columnVisibility),
    [p, columnVisibility],
  );
  const handleToggleColumn = createHrHandleToggleColumn(setColumnVisibility, HR_TABLE_COLUMNS);
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => isHrColumnVisible(columnVisibility, key);
  const tableColSpan = hrTableColSpan(columnVisibility);

  const importExportProps = useMemo(
    () => buildHrImportExportProps({
      showImportDropdown,
      setShowImportDropdown,
      showExportDropdown,
      setShowExportDropdown,
      importDropdownRef,
      exportDropdownRef,
      p,
      t,
      isGeneratingPDF,
      isGeneratingExcel,
      handlers: {
        onImport: handleImport,
        onExportCSV: handleExportCSV,
        onExportPDF: handleExportPDF,
        onExportExcel: handleExportExcel,
      },
    }),
    [showImportDropdown, showExportDropdown, isGeneratingPDF, isGeneratingExcel, t],
  );

  const openNewEmployeeModal = () => {
    setEditingRecord(null);
    setFormData(buildEmptyEmployeeForm(previewHrEmployeeMatricule(hrConfig.employeeReferencePrefix, employees)));
    setFormTab('personalInfo');
    setShowModal(true);
  };

  
  const employeeFormTabs = useMemo(() => ([
    { id: 'personalInfo', label: p('section_personal'), icon: Users, iconClassName: 'text-blue-400' },
    { id: 'professionalInfo', label: p('section_professional'), icon: Briefcase, iconClassName: 'text-green-400' },
    { id: 'employment', label: p('section_contract'), icon: FileText, iconClassName: 'text-purple-400' },
  ]), [p]);

  const advancedOptionsContent = (
    <div className="space-y-6 pt-6">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
            >
              <tab.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
            </button>
          ))}
        </div>
      </div>

      {mainViewMode === 'all_employees' && (
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
          <div className="flex space-x-1 overflow-x-auto table-scroll">
            {quickFilterButtons.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setQuickFilter(filter.id)}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
              >
                <filter.icon className="h-5 w-5" />
                <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
          {activeDistributionFilter && (
            <button
              onClick={() => setActiveDistributionFilter(null)}
              className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
            >
              <XCircle size={14} />
              {p('reset_filter')}
            </button>
          )}
        </div>
        <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
            <button
              key={item.filterKey || item.name}
              onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.filterKey || item.name) ? null : (item.filterKey || item.name))}
              className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${
                activeDistributionFilter === (item.filterKey || item.name)
                  ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20'
                  : 'border-white/10 hover:border-white/20 hover:bg-white/10'
              }`}
              title={p('filter_by_tooltip', { name: item.name })}
            >
              <div className="flex justify-between items-center mb-2">
                <h4 className={`font-medium ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
              </div>
              <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                <div
                  className={`h-3 rounded-full ${item.color} transition-all`}
                  style={{
                    width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%`,
                  }}
                />
              </div>
              <p className={`mt-2 text-sm ${activeDistributionFilter === (item.filterKey || item.name) ? 'text-white/80' : 'text-white/60'}`}>
                {(() => {
                  const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                  const pct = total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  return p('pct_of_employees', { pct });
                })()}
              </p>
            </button>
          ))}
        </div>
      </motion.div>
    </div>
  );

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/hr/employees/${recordId}`, { status: newStatus });
      await refreshEmployees();
      dispatchHrEmployeesRefresh();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };


  return (
    <>
      <FacturationListPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Users}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={
          <HrBulkDeleteFilterAction
            count={selectedRecords.length}
            onDelete={handleBulkDelete}
            label={p('delete_count', { count: selectedRecords.length })}
          />
        }
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder')}
        columns={qawaemColumns}
        visibleColumnCount={qawaemVisibleColumnCount}
        totalColumnCount={qawaemColumns.length}
        onCreate={openNewEmployeeModal}
        createLabel={p('new_employee')}
        importExport={importExportProps}
        headerActions={
          <button type="button" onClick={refreshEmployees} className="refresh-button" title={p('refresh')}>
            <RefreshCcw size={20} />
          </button>
        }
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={buildHrControlsStrip({
          qawaem,
          viewMode,
          onViewModeChange: setViewMode,
          isDashboardLightMode,
          searchTerm,
          onSearchChange: setSearchTerm,
          searchPlaceholder: p('search_placeholder'),
          cardsTitle: p('cards_view_title'),
          tableTitle: p('table_view_title'),
        })}
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className={facturationTableHeadClass(qawaem)}>
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  
                  {isColVisible('mat') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_matricule')}</th>}
                  {isColVisible('nom') && (
                  <TableColumnHeaderFilter
                    columnKey="nom"
                    label={p('col_nom')}
                    value={colFilters.nom ?? ''}
                    onChange={(v) => setColFilter('nom', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('prenom') && (
                  <TableColumnHeaderFilter
                    columnKey="prenom"
                    label={p('col_prenom')}
                    value={colFilters.prenom ?? ''}
                    onChange={(v) => setColFilter('prenom', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('fonction') && (
                  <TableColumnHeaderFilter
                    columnKey="fonction"
                    label={p('col_fonction')}
                    value={colFilters.fonction ?? ''}
                    onChange={(v) => setColFilter('fonction', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('affectation') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_department')}</th>}
                  {isColVisible('dateRecrutement') && (
                  <TableColumnHeaderFilter
                    columnKey="dateRecrutement"
                    label={p('col_recruitment_date')}
                    value={colFilters.dateRecrutement ?? ''}
                    onChange={(v) => setColFilter('dateRecrutement', v)}
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  {isColVisible('typeContrat') && <th className="px-6 py-4 text-left text-white font-semibold">{p('col_contract')}</th>}
                  {isColVisible('status') && (
                  <TableColumnHeaderFilter
                    columnKey="status"
                    label={p('col_status')}
                    kind="select"
                    options={employeeStatusFilterOptions}
                    value={colFilters.status ?? ''}
                    onChange={(v) => setColFilter('status', v)}
                    align="center"
                    editingKey={editingKey}
                    onEditingKeyChange={setEditingKey}
                    thClassName="text-white font-semibold py-4"
                  />
                  )}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredEmployees.length > 0 ? (
                  filteredEmployees.map((record, index) => (
                    <tr 
                      key={record.id} 
                      className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}
                    >
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedRecords.includes(record.id)}
                            onChange={() => handleSelectRecord(record.id)}
                          />
                        </div>
                      </td>
                      
                      {isColVisible('mat') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.mat || '-'}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {record.email || p('no_email')}
                          </span>
                        </div>
                      </td>
                      )}
                      {isColVisible('nom') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.nom}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {record.telephone || p('no_phone')}
                          </span>
                        </div>
                      </td>
                      )}
                      {isColVisible('prenom') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.prenom}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {record.sexe || p('na')} | {p('children_count', { count: record.enfants || 0 })}
                          </span>
                        </div>
                      </td>
                      )}
                      {isColVisible('fonction') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.fonction || '-'}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {record.categorieProfessionnelle || p('no_category')}
                          </span>
                        </div>
                      </td>
                      )}
                      {isColVisible('affectation') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{getDepartmentName(record.affectation)}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {record.posteOccupe || p('no_position')}
                          </span>
                        </div>
                      </td>
                      )}
                      {isColVisible('dateRecrutement') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5 text-white font-semibold">
                            <Clock size={14} className="text-white/50" />
                            {record.dateRecrutement ? formatHrDate(record.dateRecrutement, i18n.language) : '-'}
                          </div>
                          <span className="text-white/50 text-xs mt-0.5">
                            {record.dateRecrutement ? p('months_ago', { count: Math.floor((Date.now() - new Date(record.dateRecrutement).getTime()) / (1000 * 60 * 60 * 24 * 30)) }) : p('unknown_date')}
                          </span>
                        </div>
                      </td>
                      )}
                      {isColVisible('typeContrat') && (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{getContractLabel(record.typeContrat)}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {record.periodeEssai ? p('trial_prefix', { value: record.periodeEssai }) : p('no_trial')}
                          </span>
                        </div>
                      </td>
                      )}
                      {isColVisible('status') && (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                        </div>
                      </td>
                      )}
                      <QawaemTableStickyActionsTd>
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                        />
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Users size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_state')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Card View */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredEmployees.length > 0 ? (
              filteredEmployees.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Users size={20} className="text-blue-400" />
                          {record.prenom} {record.nom}
                        </h3>
                        <div className="flex">
                          <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                        </div>
                        {record.mat && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-white/20 text-white/80">
                            {record.mat}
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Informations Personnelles */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_personal')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('card_birth_date')}</span> {record.dateNaissance || p('na')}</div>
                            <div><span className="font-medium">{p('card_place')}</span> {record.lieuNaissance || p('na')}</div>
                            <div><span className="font-medium">{p('card_gender')}</span> {getGenderLabel(record.sexe)}</div>
                            <div><span className="font-medium">{p('card_situation')}</span> {getMaritalLabel(record.situationFamiliale)}</div>
                            <div><span className="font-medium">{p('card_children')}</span> {record.enfants || 0}</div>
                            <div className="pt-2 border-t border-white/20">
                              <span className="font-medium text-white">{p('card_address_label')}</span>
                              <div className="text-white/90 text-xs mt-1">{record.adresse || p('not_available')}</div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Informations Professionnelles */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_professional')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="flex items-center gap-2">
                              <Briefcase size={16} className="text-blue-400" />
                              <span className="font-medium">{p('card_function')}</span> <span className="text-white">{record.fonction || p('na')}</span>
                            </div>
                            <div><span className="font-medium">{p('card_position')}</span> {record.posteOccupe || p('na')}</div>
                            <div><span className="font-medium">{p('card_category')}</span> {record.categorieProfessionnelle || p('na')}</div>
                            <div className="flex items-center gap-2">
                              <Building size={16} className="text-purple-400" />
                              <span className="font-medium">{p('card_department')}</span> <span className="text-white">{getDepartmentName(record.affectation)}</span>
                            </div>
                            <div><span className="font-medium">{p('card_location')}</span> {record.emplacement || p('na')}</div>
                            <div className="pt-2 border-t border-white/20">
                              <span className="font-medium">{p('card_recruitment')}</span>
                              <div className="text-white/90 text-xs mt-1">
                                {record.dateRecrutement ? formatHrDate(record.dateRecrutement, i18n.language) : p('not_available')}
                              </div>
                            </div>
                            {record.canApprove && (
                              <div className="pt-2 border-t border-white/20">
                                <div className="flex items-center gap-2 text-blue-300 text-sm">
                                  <span className="w-2 h-2 rounded-full bg-blue-400"></span>
                                  <span className="font-medium">{p('approval_responsible')}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Informations Contrat */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_contract')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div className="p-2 bg-blue-500/10 rounded border border-blue-500/30">
                              <span className="font-medium text-blue-300 text-xs">{p('card_contract_type')}</span>
                              <p className="text-blue-200 text-xs mt-1">{getContractLabel(record.typeContrat)}</p>
                            </div>
                            {record.periodeEssai && (
                              <div><span className="font-medium">{p('card_trial_period')}</span> {record.periodeEssai}</div>
                            )}
                            {record.finEssai && (
                              <div><span className="font-medium">{p('card_trial_end')}</span> {record.finEssai}</div>
                            )}
                            {record.gosiNumber && (
                              <div><span className="font-medium">{p('card_gosi')}</span> {record.gosiNumber}</div>
                            )}
                            {record.iban && (
                              <div><span className="font-medium">{p('card_iban')}</span> {record.iban}</div>
                            )}
                          </div>
                        </div>
                        
                        {/* Informations Salariales */}
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_salary')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {record.salaireBase ? (
                              <div><span className="font-medium">{p('card_base_salary')}</span> {formatSalaryDisplay(record.salaireBase)}</div>
                            ) : (
                              <div><span className="font-medium">{p('card_salary')}</span> {p('not_provided')}</div>
                            )}
                            {record.salaireNetMensuel ? (
                              <div className="p-2 bg-green-500/10 rounded border border-green-500/30">
                                <span className="font-medium text-green-300 text-xs">{p('card_net_salary')}</span>
                                <p className="text-green-200 text-sm mt-1 font-bold">{formatSalaryDisplay(record.salaireNetMensuel)}</p>
                              </div>
                            ) : null}
                            {record.indemniteTransport ? (
                              <div><span className="font-medium">{p('card_transport')}</span> {formatSalaryDisplay(record.indemniteTransport)}</div>
                            ) : null}
                            {record.indemnitePanier ? (
                              <div><span className="font-medium">{p('card_meal')}</span> {formatSalaryDisplay(record.indemnitePanier)}</div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Users size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_state')}</p>
              </div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {/* View Modal - Shows 100% of data */}
      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => { setShowViewModal(false); setViewingRecord(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('modal_view_title')}</h2>
                <button onClick={() => { setShowViewModal(false); setViewingRecord(null); }} className="text-white/70 hover:text-white transition-colors">
                  <X size={24} />
                </button>
              </div>

              <div >
                {/* Personal Info */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Users size={18} className="text-blue-400" />
                    {p('section_personal')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_matricule')}</p>
                      <p className="text-white font-medium">{viewingRecord.mat || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('view_full_name')}</p>
                      <p className="text-white font-medium text-lg">{viewingRecord.prenom} {viewingRecord.nom}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('view_birth_date')}</p>
                      <p className="text-white font-medium">{viewingRecord.dateNaissance || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('view_birth_place')}</p>
                      <p className="text-white font-medium">{viewingRecord.lieuNaissance || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_gender')}</p>
                      <p className="text-white font-medium">{getGenderLabel(viewingRecord.sexe)}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('view_marital_status')}</p>
                      <p className="text-white font-medium">{getMaritalLabel(viewingRecord.situationFamiliale)}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('view_children')}</p>
                      <p className="text-white font-medium">{viewingRecord.enfants || 0}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_status')}</p>
                      <div className="flex"><QawaemClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div>
                    </div>
                  </div>
                </div>

                {/* Professional Info */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Briefcase size={18} className="text-green-400" />
                    {p('section_professional')}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_function')}</p>
                      <p className="text-white font-medium">{viewingRecord.fonction || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('view_position_held')}</p>
                      <p className="text-white font-medium">{viewingRecord.posteOccupe || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('view_prof_category')}</p>
                      <p className="text-white font-medium">{viewingRecord.categorieProfessionnelle || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_department')}</p>
                      <p className="text-white font-medium">{getDepartmentName(viewingRecord.affectation)}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_location')}</p>
                      <p className="text-white font-medium">{viewingRecord.emplacement || p('na')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_recruitment_date')}</p>
                      <p className="text-white font-medium">
                        {viewingRecord.dateRecrutement ? formatHrDate(viewingRecord.dateRecrutement, i18n.language) : p('na')}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{p('label_contract_type')}</p>
                      <p className="text-white font-medium">{getContractLabel(viewingRecord.typeContrat)}</p>
                    </div>
                  </div>
                </div>

                {/* Grid layout for Contact and Salary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Contact */}
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('section_contact')}</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_phone')}</p>
                        <p className="text-white font-medium">{viewingRecord.telephone || p('na')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_email')}</p>
                        <p className="text-white font-medium">{viewingRecord.email || p('na')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_address')}</p>
                        <p className="text-white font-medium">{viewingRecord.adresse || p('not_provided_f')}</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_city')}</p>
                        <p className="text-white font-medium">{viewingRecord.ville || p('na')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Salary */}
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4">{p('section_salary')}</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_base_salary')}</p>
                        <p className="text-white font-medium">
                          {formatSalaryDisplay(viewingRecord.salaireBase) || p('not_provided')}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_net_salary')}</p>
                        <p className="text-white font-medium text-lg">
                          {formatSalaryDisplay(viewingRecord.salaireNetMensuel) || p('not_provided')}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_transport_allowance')}</p>
                        <p className="text-white font-medium">
                          {formatSalaryDisplay(viewingRecord.indemniteTransport) || p('na')}
                        </p>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{p('label_meal_allowance')}</p>
                        <p className="text-white font-medium">
                          {formatSalaryDisplay(viewingRecord.indemnitePanier) || p('na')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Create/Edit Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" style={{ top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" 
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}
          >
            {/* Fixed Header */}
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>{editingRecord ? p('modal_edit_title') : p('modal_create_title')}</h2>
              <div className="flex items-center gap-3">
                <button 
                  type="submit" 
                  form="employee-form" 
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {editingRecord ? p('btn_update') : p('btn_create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}>
                  <X size={24} />
                </button>
              </div>
            </div>
            
            
            <HrFormModalTabBar
              tabs={employeeFormTabs}
              activeTab={formTab}
              onTabChange={setFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingRecord ? p('modal_edit_title') : p('modal_create_title')}
            />

            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="employee-form" onSubmit={handleSubmit} >
                {formTab === 'personalInfo' && (
                  <>
                    {/* Profile Picture Section */}
                <div className="flex justify-center mb-6">
                  <div className="relative group">
                    <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-blue-500/30 bg-gradient-to-br from-blue-500/20 to-purple-600/20 flex items-center justify-center">
                      {formData.profilePicture ? (
                        <img 
                          src={formData.profilePicture} 
                          alt={p('profile_photo_alt')}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User size={48} className="text-white/40" />
                      )}
                    </div>
                    <label 
                      htmlFor="profile-picture-upload" 
                      className="absolute bottom-0 right-0 p-2 bg-blue-500 hover:bg-blue-600 rounded-full cursor-pointer transition-all shadow-lg"
                      title={p('change_photo')}
                    >
                      <Camera size={18} className="text-white" />
                    </label>
                    <input
                      id="profile-picture-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            setFormData({ ...formData, profilePicture: reader.result });
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                  </div>
                </div>

                
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                              {p('label_matricule')}
                              <span className="text-white/40 text-xs ml-2">{p('mat_auto_generated')}</span>
                            </label>
                            <input 
                              type="text" 
                              value={formData.mat} 
                              onChange={(e) => setFormData({ ...formData, mat: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={p('ph_mat')} 
                            />
                            <p className="text-white/30 text-xs mt-1">{p('mat_format_hint')}</p>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_nom')} required />}</label>
                            <input 
                              type="text" 
                              required 
                              value={formData.nom} 
                              onChange={(e) => setFormData({ ...formData, nom: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={p('placeholder_last_name')} 
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{<RequiredLabelText text={p('label_prenom')} required />}</label>
                            <input 
                              type="text" 
                              required 
                              value={formData.prenom} 
                              onChange={(e) => setFormData({ ...formData, prenom: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={p('placeholder_first_name')} 
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_birth_date')}</label>
                            <CustomDatePicker
                              value={formData.dateNaissance}
                              onChange={(value) => setFormData({ ...formData, dateNaissance: value })}
                              placeholder={p('placeholder_date')}
                              minDate={new Date(new Date().getFullYear() - 120, 0, 1)}
                              maxDate={new Date()}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_birth_place')}</label>
                            <input 
                              type="text" 
                              value={formData.lieuNaissance} 
                              onChange={(e) => setFormData({ ...formData, lieuNaissance: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={p('placeholder_birth_place')} 
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_gender')}</label>
                            <CustomSelect
                              value={formData.sexe}
                              onChange={(value) => setFormData({ ...formData, sexe: value })}
                              options={[
                                { value: '', label: p('select') },
                                { value: 'M', label: p('gender_male') },
                                { value: 'F', label: p('gender_female') }
                              ]}
                              placeholder={p('select')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_marital_status')}</label>
                            <CustomSelect
                              value={formData.situationFamiliale}
                              onChange={(value) => setFormData({ ...formData, situationFamiliale: value })}
                              options={[
                                { value: '', label: p('select') },
                                { value: 'single', label: p('marital_single') },
                                { value: 'married', label: p('marital_married') },
                                { value: 'divorced', label: p('marital_divorced') },
                                { value: 'widowed', label: p('marital_widowed') }
                              ]}
                              placeholder={p('select')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_children_count')}</label>
                            <CustomNumberInput
                              value={formData.enfants}
                              onChange={(value) => setFormData({ ...formData, enfants: value })}
                              min={0}
                              step={1}
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_phone')}</label>
                            <input 
                              type="tel" 
                              value={formData.telephone} 
                              onChange={(e) => setFormData({ ...formData, telephone: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={phonePlaceholder} 
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_email')}</label>
                            <input 
                              type="email" 
                              value={formData.email} 
                              onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={p('placeholder_email')} 
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_address')}</label>
                            <input 
                              type="text" 
                              value={formData.adresse} 
                              onChange={(e) => setFormData({ ...formData, adresse: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={p('placeholder_address')} 
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_city')}</label>
                            <CustomSelect
                              value={formData.ville}
                              onChange={(value) => setFormData({ ...formData, ville: value })}
                              options={[
                                { value: '', label: p('select') },
                                ...SAUDI_CITIES.map((city) => ({ value: city, label: city })),
                              ]}
                              placeholder={p('select')}
                              searchable
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_nationality_type')}</label>
                            <CustomSelect
                              value={formData.nationalityType || 'saudi'}
                              onChange={(value) => setFormData({ ...formData, nationalityType: value })}
                              options={[
                                { value: 'saudi', label: t('modules.ressources_humaines.shared.nationality_saudi') },
                                { value: 'expatriate', label: t('modules.ressources_humaines.shared.nationality_expatriate') },
                              ]}
                              placeholder={p('select')}
                            />
                          </div>
                          {formData.nationalityType === 'expatriate' ? (
                            <>
                              <div>
                                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_iqama')}</label>
                                <input type="text" value={formData.iqamaNumber || ''} onChange={(e) => setFormData({ ...formData, iqamaNumber: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={t('modules.ressources_humaines.shared.ph_iqama')} />
                              </div>
                              <div>
                                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_passport')}</label>
                                <input type="text" value={formData.passportNumber || ''} onChange={(e) => setFormData({ ...formData, passportNumber: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={t('modules.ressources_humaines.shared.ph_passport')} />
                              </div>
                              <div>
                                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_sponsorship')}</label>
                                <input type="text" value={formData.sponsorship || ''} onChange={(e) => setFormData({ ...formData, sponsorship: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={t('modules.ressources_humaines.shared.ph_sponsorship')} />
                              </div>
                              <div>
                                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_work_permit')}</label>
                                <input type="text" value={formData.workPermitNumber || ''} onChange={(e) => setFormData({ ...formData, workPermitNumber: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={t('modules.ressources_humaines.shared.ph_work_permit')} />
                              </div>
                              <div>
                                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_work_permit_expiry')}</label>
                                <CustomDatePicker value={formData.workPermitExpiry} onChange={(value) => setFormData({ ...formData, workPermitExpiry: value })} placeholder={p('select_date')} />
                              </div>
                              <div>
                                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_exit_reentry')}</label>
                                <input type="text" value={formData.exitReentryVisa || ''} onChange={(e) => setFormData({ ...formData, exitReentryVisa: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
                              </div>
                            </>
                          ) : (
                            <div>
                              <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_national_id')}</label>
                              <input type="text" value={formData.nationalId || ''} onChange={(e) => setFormData({ ...formData, nationalId: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={t('modules.ressources_humaines.shared.ph_national_id')} />
                            </div>
                          )}
                    </div>
                  </>
                )}
                {formTab === 'professionalInfo' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_function')}</label>
                            <input 
                              type="text" 
                              value={formData.fonction} 
                              onChange={(e) => setFormData({ ...formData, fonction: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={p('placeholder_function')} 
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_position')}</label>
                            <input 
                              type="text" 
                              value={formData.posteOccupe} 
                              onChange={(e) => setFormData({ ...formData, posteOccupe: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={p('placeholder_position')} 
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_prof_category')}</label>
                            <input 
                              type="text" 
                              value={formData.categorieProfessionnelle} 
                              onChange={(e) => setFormData({ ...formData, categorieProfessionnelle: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={p('placeholder_category')} 
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_department')}</label>
                            <CustomSelect
                              value={formData.affectation}
                              onChange={(value) => setFormData({ ...formData, affectation: value })}
                              options={[
                                { value: '', label: p('select') },
                                ...departments.map(dept => ({
                                  value: dept.id,
                                  label: dept.name
                                }))
                              ]}
                              placeholder={p('select')}
                              searchable={true}
                              creatable={true}
                            />
                            <p className="text-white/50 text-xs mt-1">{p('dept_select_hint')}</p>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_recruitment_date')}</label>
                            <CustomDatePicker
                              value={formData.dateRecrutement}
                              onChange={(value) => setFormData({ ...formData, dateRecrutement: value })}
                              placeholder={p('select_date')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_exit_date')}</label>
                            <CustomDatePicker
                              value={formData.dateSortie}
                              onChange={(value) => setFormData({ ...formData, dateSortie: value })}
                              placeholder={p('select_date')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_location')}</label>
                            <input 
                              type="text" 
                              value={formData.emplacement || ''} 
                              onChange={(e) => setFormData({ ...formData, emplacement: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={p('placeholder_location')} 
                            />
                          </div>
                          <div>
                            <div className="flex items-center gap-3 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl h-full">
                              <input 
                                type="checkbox" 
                                id="canApprove"
                                checked={formData.canApprove || false}
                                onChange={(e) => setFormData({ ...formData, canApprove: e.target.checked })}
                                className="w-5 h-5 rounded border-white/30 bg-white/10 text-blue-500 focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer"
                              />
                              <label htmlFor="canApprove" className="text-white font-medium text-sm cursor-pointer select-none">
                                <span className="text-blue-300">{p('approval_responsible')}</span>
                                <span className="block text-white/60 text-xs mt-1">
                                  {p('approval_hint')}
                                </span>
                              </label>
                            </div>
                          </div>
                    </div>
                  </>
                )}
                {formTab === 'employment' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_contract_type')}</label>
                            <CustomSelect
                              value={formData.typeContrat}
                              onChange={(value) => setFormData({ ...formData, typeContrat: value })}
                              options={contractOptions}
                              placeholder={p('select')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_trial_period')}</label>
                            <input 
                              type="text" 
                              value={formData.periodeEssai} 
                              onChange={(e) => setFormData({ ...formData, periodeEssai: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={p('placeholder_trial_period')}
                            />
                            <p className="text-white/40 text-xs mt-1">{t('modules.ressources_humaines.shared.probation_hint')}</p>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_trial_end')}</label>
                            <CustomDatePicker
                              value={formData.finEssai}
                              onChange={(value) => setFormData({ ...formData, finEssai: value })}
                              placeholder={p('select_date')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_gosi')}</label>
                            <input 
                              type="text" 
                              value={formData.gosiNumber || ''} 
                              onChange={(e) => setFormData({ ...formData, gosiNumber: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={t('modules.ressources_humaines.shared.ph_gosi')} 
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_iban')}</label>
                            <input 
                              type="text" 
                              value={formData.iban || ''} 
                              onChange={(e) => setFormData({ ...formData, iban: e.target.value })} 
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
                              placeholder={t('modules.ressources_humaines.shared.ph_iban')} 
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_nitaqat')}</label>
                            <CustomSelect
                              value={formData.nitaqatCategory || ''}
                              onChange={(value) => setFormData({ ...formData, nitaqatCategory: value })}
                              options={[
                                { value: '', label: p('select') },
                                ...NITAQAT_CATEGORIES.map((c) => ({ value: c.value, label: c.label })),
                              ]}
                              placeholder={p('select')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')}</label>
                            <CustomSelect
                              value={formData.status}
                              onChange={(value) => setFormData({ ...formData, status: value })}
                              options={[
                                { value: 'active', label: p('status_active') },
                                { value: 'leave', label: p('status_leave') },
                                { value: 'inactive', label: p('status_inactive') }
                              ]}
                              placeholder={p('select')}
                            />
                          </div>
                    </div>
                  </>
                )}
                {formTab === 'employment' && (
                  <>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_base_salary')}</label>
                            <CustomNumberInput
                              value={formData.salaireBase}
                              onChange={(value) => setFormData({ ...formData, salaireBase: value })}
                              step={0.01}
                              min={0}
                              decimals={2}
                              placeholder="8000"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_net_salary')}</label>
                            <CustomNumberInput
                              value={formData.salaireNetMensuel}
                              onChange={(value) => setFormData({ ...formData, salaireNetMensuel: value })}
                              step={0.01}
                              min={0}
                              decimals={2}
                              placeholder="12000"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_experience_allowance')}</label>
                            <CustomNumberInput
                              value={formData.indemniteExperience}
                              onChange={(value) => setFormData({ ...formData, indemniteExperience: value })}
                              step={0.01}
                              min={0}
                              decimals={2}
                              placeholder="500"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_transport_allowance')}</label>
                            <CustomNumberInput
                              value={formData.indemniteTransport}
                              onChange={(value) => setFormData({ ...formData, indemniteTransport: value })}
                              step={0.01}
                              min={0}
                              decimals={2}
                              placeholder="1500"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_meal_allowance')}</label>
                            <CustomNumberInput
                              value={formData.indemnitePanier}
                              onChange={(value) => setFormData({ ...formData, indemnitePanier: value })}
                              step={0.01}
                              min={0}
                              decimals={2}
                              placeholder="800"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_responsibility_bonus')}</label>
                            <CustomNumberInput
                              value={formData.primeResponsabilite}
                              onChange={(value) => setFormData({ ...formData, primeResponsabilite: value })}
                              step={0.01}
                              min={0}
                              decimals={2}
                              placeholder="2000"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_individual_bonus')}</label>
                            <CustomNumberInput
                              value={formData.primeRendementIndividuel}
                              onChange={(value) => setFormData({ ...formData, primeRendementIndividuel: value })}
                              step={0.01}
                              min={0}
                              decimals={2}
                              placeholder="1500"
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_collective_bonus')}</label>
                            <CustomNumberInput
                              value={formData.primeRendementCollectif}
                              onChange={(value) => setFormData({ ...formData, primeRendementCollectif: value })}
                              step={0.01}
                              min={0}
                              decimals={2}
                              placeholder="1000"
                            />
                          </div>
                    </div>
                  </>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </>
  );
};

export default EmployeesPage;

